import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SuiDropdownOption } from '@splashthat/sui';

import { getSelectedSocialRsvp } from 'common/entities/formNode/selectors';
import { useFlags } from 'utils/react-context';

import { PrepopulatedTitle, DescriptionText, PrepoulatedHeader, SocialNetworkDropdown } from './index.styles';

import SectionSeparator from '../components/SectionSeparator';
import { setSocialRsvpEnabled } from '../../../actions/socialRsvp';
import { getFormId, getSocialNetworkFields } from '../../../selectors';
import RequiredControl from '../components/RequiredControl';
import EditViewSectionContainer from '../EditViewSectionContainer';
import SelectFormNode from './SelectFormNode';

const LINKEDIN = 'linkedIn';
const FACEBOOK = 'facebook';

const socialNetworks = [LINKEDIN, FACEBOOK];

const PREPOPULATED_TITLE = 'Map to Custom Questions';
const RSVP_TYPE_LABEL = 'RSVP Type';

const socialNetworkFieldLabels = {
    linkedInCompany: 'Company',
    linkedInJobTitle: 'Job Title',
    linkedInProfileUrl: 'Profile Url',
    linkedInProfilePicture: 'Profile Picture',
    linkedInLocation: 'Location',
    facebookFacebookId: 'Facebook ID',
    facebookMiddleName: 'Middle Name',
    facebookShortName: 'Short Name',
};

const socialNetworkLabels = {
    [FACEBOOK]: 'Facebook',
    [LINKEDIN]: 'LinkedIn',
};

const socialNetworkText = {
    [LINKEDIN]:
        'When a guest RSVPs with LinkedIn, we will store their first name, last name, email address, location, company, job title, and profile picture.',
    [FACEBOOK]:
        'When a guest RSVPs with Facebook, we will store their first name, last name, email, Facebook ID, middle name, and short name.',
};

const socialNetworkFieldsText = {
    [LINKEDIN]: 'Set LinkedIn information to populate your own custom questions.',
    [FACEBOOK]: 'Set Facebook information to populate your own custom questions.',
};

const nonPrepopulatedFields = [
    'linkedInFirstName',
    'linkedInLastName',
    'linkedInEmail',
    'facebookFirstName',
    'facebookLastName',
    'facebookEmail',
];

const SocialRsvp = (props) => {
    const flags = useFlags();
    const dispatch = useDispatch();
    const formId = useSelector(getFormId);
    const selectedSocialNetwork = useSelector((state) => getSelectedSocialRsvp(state, formId));
    const selectedSocialNetworkFields = useSelector((state) => getSocialNetworkFields(state, selectedSocialNetwork));

    const selectSocialRsvp = React.useCallback(
        (selectedSocialRsvp) => {
            dispatch(setSocialRsvpEnabled(selectedSocialRsvp, true));
            socialNetworks
                .filter((socialNetwork) => socialNetwork !== selectedSocialRsvp)
                .forEach((socialNetwork) => {
                    dispatch(setSocialRsvpEnabled(socialNetwork, false));
                });
        },
        [dispatch],
    );

    return (
        <div>
            <RequiredControl disabled={false} />
            <EditViewSectionContainer>
                {flags.showFacebookSocialRsvp && (
                    <SocialNetworkDropdown
                        value={selectedSocialNetwork}
                        label={RSVP_TYPE_LABEL}
                        onChange={(_, socialRsvp) => selectSocialRsvp(socialRsvp)}
                    >
                        {socialNetworks.map((socialNetwork) => (
                            <SuiDropdownOption
                                key={socialNetwork}
                                value={socialNetwork}
                                displayValue={socialNetworkLabels[socialNetwork]}
                            />
                        ))}
                    </SocialNetworkDropdown>
                )}
                <DescriptionText>{socialNetworkText[selectedSocialNetwork]}</DescriptionText>

                {selectedSocialNetwork && (
                    <div>
                        <SectionSeparator />
                        <div>
                            <PrepoulatedHeader>
                                <PrepopulatedTitle>{PREPOPULATED_TITLE}</PrepopulatedTitle>
                                <DescriptionText>{socialNetworkFieldsText[selectedSocialNetwork]}</DescriptionText>
                            </PrepoulatedHeader>

                            {selectedSocialNetworkFields
                                .filter((field) => !nonPrepopulatedFields.includes(field))
                                .map((field) => (
                                    <SelectFormNode
                                        key={field}
                                        label={socialNetworkFieldLabels[field] || field}
                                        socialNetworkOption={field}
                                    />
                                ))}
                        </div>
                    </div>
                )}
            </EditViewSectionContainer>
        </div>
    );
};

export default SocialRsvp;
